import React, { useEffect, useRef } from 'react';
import { Space, Button } from 'antd';

import {
    LineOutlined,
    BoldOutlined,
    ItalicOutlined,
    StrikethroughOutlined,
    OrderedListOutlined,
    UnorderedListOutlined,
    CodeOutlined,
    CheckSquareOutlined,
    ClearOutlined,
    InfoCircleOutlined,
    PictureOutlined,
    VideoCameraOutlined,
} from '@ant-design/icons';

import Code from '@tiptap/extension-code';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import StarterKit from '@tiptap/starter-kit';
import BulletList from '@tiptap/extension-bullet-list';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { EditorHighlighter } from './EditorHighlighter';
import { EditorTrailingNode } from './EditorTrailingNode';
import { EditorLinkFavicons } from './EditorLinkFavicons';
import { EditorVideo } from './EditorVideo';
import { linkifyHtml } from '../utils/links';
import { isVideoFile, MEDIA_ACCEPT } from '../utils/media';
import { MAX_TASK_IMAGES_MESSAGE, MAX_TASK_VIDEOS_MESSAGE } from '../constants';

import { useEditor, EditorContent } from '@tiptap/react';
import useEventListener from '../hooks/useEventListener';

interface EditorProps {
    /** Начальный HTML (режим редактирования в карточке) */
    initialContent?: string;
    /** Controlled value для Ant Form */
    value?: string;
    onChange?: (html: string) => void;
    onSave?: (content: string) => void;
    onCancel?: () => void;
    /** Загрузка картинки/видео → URL для вставки в текст (и дубль в «Файлы») */
    onUploadImage?: (file: File) => Promise<string | null>;
    /** Кнопка вставки картинки недоступна (лимит вложений) */
    disableImageUpload?: boolean;
    /** Кнопка вставки видео недоступна (лимит вложений) */
    disableVideoUpload?: boolean;
    /** Показывать кнопки Отмена/Сохранить */
    showActions?: boolean;
    className?: string;
}

const CustomBulletList = BulletList.extend({
    addKeyboardShortcuts() {
        return {
             'Ctrl-Enter'() {
                return true;
            },
        }
    },
});

/** Блочная картинка: можно выделить кликом и удалить Backspace/Delete */
const CustomImage = Image.extend({
    atom: true,
    selectable: true,
    draggable: true,
});

const extensions = [
    StarterKit,
    Document,
    Paragraph,
    Text,
    Code,
    EditorHighlighter,
    EditorTrailingNode,
    TaskList,
    TaskItem,
    CustomBulletList,
    CustomImage.configure({
        allowBase64: false,
        HTMLAttributes: {
            class: 'task-content-image',
        },
    }),
    Link.extend({
        inclusive: false,
    }).configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
        HTMLAttributes: {
            class: 'task-content-link',
            target: '_blank',
            rel: 'noopener noreferrer',
        },
    }),
    EditorVideo,
    EditorLinkFavicons,
];

const getFirstMediaFile = (files: FileList | File[] | null | undefined): File | null => {
    if (!files || files.length === 0) {
        return null;
    }
    const list = Array.from(files);
    return list.find((file) => file.type.startsWith('image/') || isVideoFile(file)) || null;
};

const Editor: React.FC<EditorProps> = ({
    initialContent = '',
    value,
    onChange,
    onSave,
    onCancel,
    onUploadImage,
    disableImageUpload = false,
    disableVideoUpload = false,
    showActions = true,
    className,
}) => {
    const content = value !== undefined ? value : initialContent;
    const skipSyncRef = useRef(false);
    const imageInputRef = useRef<HTMLInputElement | null>(null);
    const videoInputRef = useRef<HTMLInputElement | null>(null);
    const onUploadImageRef = useRef(onUploadImage);
    const editorRef = useRef<ReturnType<typeof useEditor>>(null);

    onUploadImageRef.current = onUploadImage;

    const insertUploadedMedia = async (file: File) => {
        const upload = onUploadImageRef.current;
        if (!upload) {
            return false;
        }
        const video = isVideoFile(file);
        const src = await upload(file);
        if (!src) {
            return false;
        }

        editorRef.current
            ?.chain()
            .focus()
            .insertContent([
                { type: video ? 'video' : 'image', attrs: { src } },
                { type: 'paragraph' },
            ])
            .run();
        return true;
    };

    const editor = useEditor({
        extensions,
        content: linkifyHtml(content || ''),
        onUpdate: ({ editor: ed }) => {
            skipSyncRef.current = true;
            onChange?.(ed.getHTML());
        },
        editorProps: {
            handlePaste(_view, event) {
                if (!onUploadImageRef.current) {
                    return false;
                }
                const file = getFirstMediaFile(event.clipboardData?.files);
                if (!file) {
                    return false;
                }
                event.preventDefault();
                void insertUploadedMedia(file);
                return true;
            },
            handleDrop(_view, event) {
                if (!onUploadImageRef.current) {
                    return false;
                }
                const file = getFirstMediaFile(event.dataTransfer?.files);
                if (!file) {
                    return false;
                }
                event.preventDefault();
                void insertUploadedMedia(file);
                return true;
            },
        },
    });

    editorRef.current = editor;

    useEffect(() => {
        if (!editor) {
            return;
        }
        if (skipSyncRef.current) {
            skipSyncRef.current = false;
            return;
        }
        const current = editor.getHTML();
        const next = linkifyHtml(content || '');
        if (next !== current) {
            editor.commands.setContent(next, false);
        }
    }, [content, editor]);

    useEventListener('keydown', (event) => {
        if (!showActions || !onSave) {
            return;
        }

        const activeElement = document.activeElement;
        const isInputElement = activeElement && (activeElement.getAttribute('contenteditable'));

        if (isInputElement) {
            if (event.key === 'Enter' && event.ctrlKey) {
                event.preventDefault();
                event.stopPropagation();
                handleSave();
            }
        }
    });

    const handleSave = () => {
        if (editor && onSave) {
            onSave(linkifyHtml(editor.getHTML()));
        }
    };

    const handlePickImage = () => {
        imageInputRef.current?.click();
    };

    const handlePickVideo = () => {
        videoInputRef.current?.click();
    };

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }
        void insertUploadedMedia(file);
    };

    return (
        <div className={['task-editor', className].filter(Boolean).join(' ')}>
            <div className="task-editor__body">
                {editor && (
                    <div className="editor-toolbar">
                        <Space>
                            <Button
                                type={editor.isActive('heading', { level: 2 }) ? 'primary' : 'text'}
                                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                            >
                                Заголовок
                            </Button>
                        </Space>

                        <div className="editor-toolbar__divider" />

                        <Space>
                            <Button
                                type={editor.isActive('bold') ? 'primary' : 'text'}
                                onClick={() => editor.chain().focus().toggleBold().run()}
                                icon={<BoldOutlined />}
                                title="Жирный текст"
                            />
                            <Button
                                type={editor.isActive('italic') ? 'primary' : 'text'}
                                onClick={() => editor.chain().focus().toggleItalic().run()}
                                icon={<ItalicOutlined />}
                                title="Курсив"
                            />
                            <Button
                                type={editor.isActive('strike') ? 'primary' : 'text'}
                                onClick={() => editor.chain().focus().toggleStrike().run()}
                                icon={<StrikethroughOutlined />}
                                title="Зачеркнутый текст"
                            />
                            <Button
                                type={editor.isActive('code') ? 'primary' : 'text'}
                                onClick={() => editor.chain().focus().toggleCode().run()}
                                icon="M"
                                title="Моношрифт"
                            />
                        </Space>

                        <div className="editor-toolbar__divider" />

                        <Space>
                            <Button
                                type="text"
                                onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                                icon={<ClearOutlined />}
                                title="Очистить форматирование"
                            />
                        </Space>

                        <div className="editor-toolbar__divider" />

                        <Space>
                            <Button
                                type={editor.isActive('taskList') ? 'primary' : 'text'}
                                onClick={() => editor.chain().focus().toggleTaskList().run()}
                                icon={<CheckSquareOutlined />}
                                title="Список задач"
                            />
                            <Button
                                type={editor.isActive('bulletList') ? 'primary' : 'text'}
                                onClick={() => editor.chain().focus().toggleBulletList().run()}
                                icon={<UnorderedListOutlined />}
                                title="Маркированный список"
                            />
                            <Button
                                type={editor.isActive('orderedList') ? 'primary' : 'text'}
                                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                                icon={<OrderedListOutlined />}
                                title="Нумерованный список"
                            />
                            <Button
                                type={editor.isActive('codeBlock') ? 'primary' : 'text'}
                                onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                                icon={<CodeOutlined />}
                                title="Блок кода"
                            />
                            <Button
                                type={editor.isActive('blockquote') ? 'primary' : 'text'}
                                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                                icon={<InfoCircleOutlined />}
                                title="Баннер"
                            />
                            <Button
                                type="text"
                                onClick={() => editor.chain().focus().setHorizontalRule().run()}
                                icon={<LineOutlined />}
                                title="Разделитель"
                            />
                            {onUploadImage && (
                                <>
                                    <Button
                                        type="text"
                                        onClick={handlePickImage}
                                        icon={<PictureOutlined />}
                                        disabled={disableImageUpload}
                                        title={disableImageUpload
                                            ? MAX_TASK_IMAGES_MESSAGE
                                            : 'Вставить изображение'}
                                    />
                                    <Button
                                        type="text"
                                        onClick={handlePickVideo}
                                        icon={<VideoCameraOutlined />}
                                        disabled={disableVideoUpload}
                                        title={disableVideoUpload
                                            ? MAX_TASK_VIDEOS_MESSAGE
                                            : 'Вставить видео'}
                                    />
                                </>
                            )}
                        </Space>
                    </div>
                )}

                {onUploadImage && (
                    <>
                        <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleFileInputChange}
                        />
                        <input
                            ref={videoInputRef}
                            type="file"
                            accept={MEDIA_ACCEPT.replace('image/*,', '')}
                            style={{ display: 'none' }}
                            onChange={handleFileInputChange}
                        />
                    </>
                )}

                <div className="task-editor__content">
                    <EditorContent editor={editor} />
                </div>
            </div>

            {showActions && (
                <div className="task-editor__actions">
                    <Space>
                        <Button onClick={onCancel}>Отмена</Button>
                        <Button type="primary" onClick={handleSave}>Сохранить</Button>
                    </Space>
                </div>
            )}
        </div>
    );
};

export default Editor;
